import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, IconButton, Button,
  TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert, Snackbar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tabs, Tab, Autocomplete, Switch, FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon, Edit as EditIcon, Delete as DeleteIcon,
  Close as CloseIcon, Refresh as RefreshIcon, HealthAndSafety as InsuranceIcon,
  AttachMoney as MoneyIcon, Assignment as AuthIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { useClinicPageBreakpoints, clinicPageContainerSx } from '../../hooks/useClinicPageBreakpoints';


const formatCurrency = (v: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const InsuranceManagement: React.FC = () => {
  const { isMobile } = useClinicPageBreakpoints();

  const [tab, setTab] = useState(0);
  const [insurances, setInsurances] = useState<any[]>([]);
  const [authorizations, setAuthorizations] = useState<any[]>([]);
  const [prices, setPrices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [selectedInsurance, setSelectedInsurance] = useState<any>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as any });

  const [form, setForm] = useState<any>({
    name: '', code: '', ans_code: '', type: 'medical', contact_name: '',
    contact_phone: '', contact_email: '', website: '', notes: '', is_active: true,
  });
  const [authForm, setAuthForm] = useState<any>({
    insurance_id: '', patient_id: '', authorization_number: '', procedure_name: '',
    procedure_code: '', request_date: new Date().toISOString().split('T')[0],
    status: 'requested', notes: '',
  });
  const [openAuthDialog, setOpenAuthDialog] = useState(false);
  const [patientOptions, setPatientOptions] = useState<any[]>([]);

  useEffect(() => { loadInsurances(); }, []);

  const loadInsurances = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${getApiUrl()}/api/clinic/insurances-advanced`, { params: { search } });
      setInsurances(data.data || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const loadPrices = async (insuranceId: string) => {
    try {
      const { data } = await axios.get(`${getApiUrl()}/api/clinic/insurances-advanced/${insuranceId}/prices`);
      setPrices(data.data || []);
    } catch (e) { console.error(e); }
  };

  const loadAuthorizations = async () => {
    try {
      const params: any = {};
      if (selectedInsurance) params.insurance_id = selectedInsurance.id;
      const { data } = await axios.get(`${getApiUrl()}/api/clinic/insurance-authorizations`, { params });
      setAuthorizations(data.data || []);
    } catch (e) { console.error(e); }
  };

  const searchPatients = async (q: string) => {
    try {
      const { data } = await axios.get(`${getApiUrl()}/api/clinic/prescriptions-search/patients`, { params: { search: q } });
      setPatientOptions(data.data || []);
    } catch (e) { console.error(e); }
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await axios.put(`${getApiUrl()}/api/clinic/insurances-advanced/${editing.id}`, form);
        setSnackbar({ open: true, message: 'Convênio atualizado!', severity: 'success' });
      } else {
        await axios.post(`${getApiUrl()}/api/clinic/insurances-advanced`, form);
        setSnackbar({ open: true, message: 'Convênio criado!', severity: 'success' });
      }
      setOpenDialog(false); loadInsurances();
    } catch (e: any) { setSnackbar({ open: true, message: e.response?.data?.error || 'Erro', severity: 'error' }); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Excluir convênio?')) return;
    try {
      await axios.delete(`${getApiUrl()}/api/clinic/insurances-advanced/${id}`);
      setSnackbar({ open: true, message: 'Excluído!', severity: 'success' }); loadInsurances();
    } catch (e) { setSnackbar({ open: true, message: 'Erro', severity: 'error' }); }
  };

  const handleSaveAuth = async () => {
    try {
      await axios.post(`${getApiUrl()}/api/clinic/insurance-authorizations`, authForm);
      setSnackbar({ open: true, message: 'Autorização criada!', severity: 'success' });
      setOpenAuthDialog(false); loadAuthorizations();
    } catch (e: any) { setSnackbar({ open: true, message: e.response?.data?.error || 'Erro', severity: 'error' }); }
  };

  const handleEdit = (ins: any) => {
    setEditing(ins);
    setForm({ ...ins });
    setOpenDialog(true);
  };

  return (
    <Box sx={clinicPageContainerSx}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>Gestão de Convênios</Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>Convênios, tabelas de preços e autorizações TISS</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={loadInsurances} sx={{ color: '#14B8A6' }}><RefreshIcon /></IconButton>
          <Button variant="contained" startIcon={<AddIcon />}
            sx={{ bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' } }}
            onClick={() => { setEditing(null); setForm({ name: '', code: '', ans_code: '', type: 'medical', contact_name: '', contact_phone: '', contact_email: '', website: '', notes: '', is_active: true }); setOpenDialog(true); }}>
            Novo Convênio
          </Button>
        </Box>
      </Box>

      <Tabs value={tab} onChange={(_, v) => { setTab(v); if (v === 2) loadAuthorizations(); }}
        sx={{ mb: 3, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 }, '& .Mui-selected': { color: '#14B8A6' }, '& .MuiTabs-indicator': { backgroundColor: '#14B8A6' } }}>
        <Tab icon={<InsuranceIcon />} iconPosition="start" label="Convênios" />
        <Tab icon={<MoneyIcon />} iconPosition="start" label="Tabelas de Preços" />
        <Tab icon={<AuthIcon />} iconPosition="start" label="Autorizações" />
      </Tabs>

      {tab === 0 && (
        <>
          <Card sx={{ mb: 3, borderRadius: 3 }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <TextField size="small" placeholder="Buscar convênio..." value={search}
                onChange={(e) => { setSearch(e.target.value); }} onKeyDown={(e) => { if (e.key === 'Enter') loadInsurances(); }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} sx={{ minWidth: 300 }} />
            </CardContent>
          </Card>
          <Card sx={{ borderRadius: 3 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Nome</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Contato</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><CircularProgress size={32} /></TableCell></TableRow>
                  ) : insurances.length === 0 ? (
                    <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: '#94a3b8' }}>Nenhum convênio cadastrado</TableCell></TableRow>
                  ) : insurances.map((ins) => (
                    <TableRow key={ins.id} hover onClick={() => { setSelectedInsurance(ins); setTab(1); loadPrices(ins.id); }} sx={{ cursor: 'pointer' }}>
                      <TableCell sx={{ fontWeight: 600 }}>{ins.name}</TableCell>
                      <TableCell>{ins.code || ins.ans_code || '-'}</TableCell>
                      <TableCell><Chip label={ins.type === 'medical' ? 'Médico' : ins.type === 'dental' ? 'Odontológico' : 'Ambos'} size="small" sx={{ bgcolor: '#14B8A615', color: '#14B8A6' }} /></TableCell>
                      <TableCell>{ins.contact_phone || ins.contact_email || '-'}</TableCell>
                      <TableCell><Chip label={ins.is_active ? 'Ativo' : 'Inativo'} size="small" sx={{ bgcolor: ins.is_active ? '#dcfce7' : '#fef2f2', color: ins.is_active ? '#22c55e' : '#ef4444' }} /></TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <IconButton size="small" onClick={() => handleEdit(ins)} sx={{ color: '#3b82f6' }}><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" onClick={() => handleDelete(ins.id)} sx={{ color: '#ef4444' }}><DeleteIcon fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </>
      )}

      {tab === 1 && (
        <Box>
          {selectedInsurance ? (
            <>
              <Card sx={{ mb: 3, borderRadius: 3, bgcolor: '#f0fdf4', border: '1px solid #14B8A622' }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#14B8A6' }}>{selectedInsurance.name}</Typography>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>Tabela de preços do convênio - {prices.length} procedimentos cadastrados</Typography>
                </CardContent>
              </Card>
              <Card sx={{ borderRadius: 3 }}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f8fafc' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Procedimento</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Preço Particular</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Preço Convênio</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Ações</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {prices.length === 0 ? (
                        <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: '#94a3b8' }}>Nenhum preço cadastrado</TableCell></TableRow>
                      ) : prices.map((p) => (
                        <TableRow key={p.id} hover>
                          <TableCell sx={{ fontWeight: 500 }}>{p.procedure_name || '-'}</TableCell>
                          <TableCell>{p.procedure_code || p.tuss_code || '-'}</TableCell>
                          <TableCell>{formatCurrency(p.procedure_default_price)}</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#14B8A6' }}>{formatCurrency(p.insurance_price)}</TableCell>
                          <TableCell align="right">
                            <IconButton size="small" sx={{ color: '#ef4444' }}
                              onClick={async () => { await axios.delete(`${getApiUrl()}/api/clinic/insurances-advanced/prices/${p.id}`); loadPrices(selectedInsurance.id); }}>
                              <DeleteIcon fontSize="small" /></IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            </>
          ) : (
            <Card sx={{ borderRadius: 3, p: 4, textAlign: 'center' }}>
              <Typography variant="body1" sx={{ color: '#94a3b8' }}>Selecione um convênio na aba "Convênios" para ver a tabela de preços</Typography>
            </Card>
          )}
        </Box>
      )}

      {tab === 2 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="contained" startIcon={<AddIcon />}
              sx={{ bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' } }}
              onClick={() => { setAuthForm({ insurance_id: selectedInsurance?.id || '', patient_id: '', authorization_number: '', procedure_name: '', procedure_code: '', request_date: new Date().toISOString().split('T')[0], status: 'requested', notes: '' }); setOpenAuthDialog(true); }}>
              Nova Autorização
            </Button>
          </Box>
          <Card sx={{ borderRadius: 3 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Nº Autorização</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Paciente</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Convênio</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Procedimento</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Data</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {authorizations.length === 0 ? (
                    <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: '#94a3b8' }}>Nenhuma autorização</TableCell></TableRow>
                  ) : authorizations.map((a) => (
                    <TableRow key={a.id} hover>
                      <TableCell sx={{ fontWeight: 600, color: '#14B8A6' }}>{a.authorization_number || '-'}</TableCell>
                      <TableCell>{a.patient_name || '-'}</TableCell>
                      <TableCell>{a.insurance_name || '-'}</TableCell>
                      <TableCell>{a.procedure_name || '-'}</TableCell>
                      <TableCell><Chip label={a.status} size="small" sx={{ fontWeight: 600 }} /></TableCell>
                      <TableCell>{a.request_date ? new Date(a.request_date + 'T12:00:00').toLocaleDateString('pt-BR') : '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </>
      )}

      {/* Insurance Form Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>{editing ? 'Editar' : 'Novo'} Convênio</Typography>
          <IconButton onClick={() => setOpenDialog(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Nome" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Grid>
            <Grid item xs={12} sm={3}><TextField fullWidth size="small" label="Código" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} /></Grid>
            <Grid item xs={12} sm={3}><TextField fullWidth size="small" label="Código ANS" value={form.ans_code} onChange={(e) => setForm({ ...form, ans_code: e.target.value })} /></Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small"><InputLabel>Tipo</InputLabel>
                <Select value={form.type} label="Tipo" onChange={(e) => setForm({ ...form, type: e.target.value })}>
                  <MenuItem value="medical">Médico</MenuItem><MenuItem value="dental">Odontológico</MenuItem><MenuItem value="both">Ambos</MenuItem>
                </Select></FormControl>
            </Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Contato" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Telefone" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Observações" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} multiline rows={2} /></Grid>
            <Grid item xs={12}><FormControlLabel control={<Switch checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} color="primary" />} label="Ativo" /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} sx={{ bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' } }}>{editing ? 'Atualizar' : 'Criar'}</Button>
        </DialogActions>
      </Dialog>

      {/* Authorization Dialog */}
      <Dialog open={openAuthDialog} onClose={() => setOpenAuthDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nova Autorização</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small"><InputLabel>Convênio</InputLabel>
                <Select value={authForm.insurance_id} label="Convênio" onChange={(e) => setAuthForm({ ...authForm, insurance_id: e.target.value })}>
                  {insurances.map((i) => <MenuItem key={i.id} value={i.id}>{i.name}</MenuItem>)}
                </Select></FormControl>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete size="small" options={patientOptions} getOptionLabel={(o: any) => o.full_name || ''}
                onInputChange={(_, v) => searchPatients(v)} onChange={(_, v) => setAuthForm({ ...authForm, patient_id: v?.id || '' })}
                onOpen={() => searchPatients('')} renderInput={(params) => <TextField {...params} label="Paciente" />}
                isOptionEqualToValue={(o: any, v: any) => o.id === v.id} />
            </Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Nº Autorização" value={authForm.authorization_number} onChange={(e) => setAuthForm({ ...authForm, authorization_number: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" type="date" label="Data" value={authForm.request_date} onChange={(e) => setAuthForm({ ...authForm, request_date: e.target.value })} InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Procedimento" value={authForm.procedure_name} onChange={(e) => setAuthForm({ ...authForm, procedure_name: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Código" value={authForm.procedure_code} onChange={(e) => setAuthForm({ ...authForm, procedure_code: e.target.value })} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAuthDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveAuth} sx={{ bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' } }}>Criar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default InsuranceManagement;
